// Generated by GitHub Copilot
import React, { useState } from "react";
import PropTypes from "prop-types";
import { FaEdit, FaTrash } from "react-icons/fa";
import { toTitleCase } from "../../../utils/textFormatters";
import EditUserForm from "../UserForm/EditUserForm";
import styles from "./UserList.module.css";
import badgeStyles from "./UserListItem.module.css";

/**
 * Individual User List Item Component
 */
const UserListItem = ({
  user,
  selected,
  onSelect,
  onEdit,
  onDelete,
  currentUser,
  viewMode = "table",
}) => {
  const [showEditForm, setShowEditForm] = useState(false);

  /**
   * Get privilege badges with appropriate styling
   */
  const getPrivilegeBadges = (privileges) => {
    if (!privileges) return null;

    const privilegeList = Object.entries(privileges)
      .filter(([key, value]) => value === true)
      .map(([key]) => key);

    if (privilegeList.length === 0)
      return (
        <span className={`${badgeStyles.badge} ${badgeStyles.badgeDisabled}`}>
          None
        </span>
      );

    return privilegeList.map((privilege) => {
      let badgeClass = badgeStyles.badge;
      if (privilege === "deity") badgeClass += ` ${badgeStyles.badgeDeity}`;
      else if (privilege === "revert") badgeClass += ` ${badgeStyles.badgeRevert}`;
      else if (privilege === "editor") badgeClass += ` ${badgeStyles.badgeEditor}`;
      else if (privilege === "create") badgeClass += ` ${badgeStyles.badgeCreate}`;
      else if (privilege === "search") badgeClass += ` ${badgeStyles.badgeSearch}`;
      else if (privilege === "archive") badgeClass += ` ${badgeStyles.badgeArchive}`;
      else badgeClass += ` ${styles.badgeDefault}`;

      return (
        <span key={privilege} className={badgeClass}>
          {toTitleCase(privilege)}
        </span>
      );
    });
  };

  /**
   * Get feature badges
   */
  const getFeatureBadges = (features) => {
    if (!features) return null;

    const featureList = Object.entries(features)
      .filter(([key, value]) => value === true)
      .map(([key]) => key);

    if (featureList.length === 0)
      return (
        <span className={`${badgeStyles.badge} ${badgeStyles.badgeDisabled}`}>
          None
        </span>
      );

    return featureList
      .slice(0, 3)
      .map((feature) => (
        <span key={feature} className={`${badgeStyles.badge} ${badgeStyles.badgeFeature}`}>
          {toTitleCase(feature)}
        </span>
      ))
      .concat(
        featureList.length > 3
          ? [
              <span key="more" className={`${badgeStyles.badge} ${badgeStyles.badgeDisabled}`}>
                +{featureList.length - 3} more
              </span>,
            ]
          : []
      );
  };

  /**
   * Handle checkbox selection
   */
  const handleSelectionChange = (event) => {
    onSelect(user.email, event.target.checked);
  };

  /**
   * Handle edit user
   */
  const handleEdit = () => {
    setShowEditForm(true);
  };

  /**
   * Handle user updated from edit form
   */
  const handleUserUpdated = (updatedUser) => {
    onEdit(updatedUser);
    setShowEditForm(false);
  };

  /**
   * Check if current user can edit this user
   */
  const canEdit = () => {
    // Deity users can edit anyone except themselves (for safety)
    if (currentUser.privileges.deity && user.email !== currentUser.email) {
      return true;
    }

    // Deity users can edit themselves but with restrictions
    if (currentUser.privileges.deity && user.email === currentUser.email) {
      return true;
    }

    return false;
  };

  /**
   * Check if current user can delete this user
   */
  const canDelete = () => {
    // Can't delete yourself
    if (user.email === currentUser.email) {
      return false;
    }

    // Deity users can delete anyone except other deities
    if (currentUser.privileges.deity) {
      return !user.privileges?.deity;
    }

    return false;
  };

  // Render table row view for desktop
  if (viewMode === "table") {
    return (
      <>
        <tr className={`${styles.tableRow} ${selected ? styles.tableRowSelected : ""}`}>
          <td className={styles.checkboxCell}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={selected}
              onChange={handleSelectionChange}
              disabled={user.email === currentUser.email} // Can't select yourself
            />
          </td>

          <td className={styles.emailCell}>
            {user.email}
            {user.email === currentUser.email && (
              <span className={styles.youBadge}>You</span>
            )}
          </td>

          <td className={styles.nameCell}>
            {user.name || <span className={styles.emptyText}>No name set</span>}
          </td>

          <td className={styles.privilegesCell}>
            {getPrivilegeBadges(user.privileges)}
          </td>

          <td className={styles.featuresCell}>
            {getFeatureBadges(user.features)}
          </td>

          <td className={styles.actionsCell}>
            <div className={styles.actionButtons}>
              <button
                className={`${styles.iconButton} ${styles.iconButtonPrimary}`}
                onClick={handleEdit}
                disabled={!canEdit()}
                title={canEdit() ? "Edit user" : "You cannot edit this user"}
              >
                <FaEdit />
              </button>

              <button
                className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                onClick={() => onDelete(user)}
                disabled={!canDelete()}
                title={
                  canDelete() ? "Delete user" : "You cannot delete this user"
                }
              >
                <FaTrash />
              </button>
            </div>
          </td>
        </tr>

        {/* Edit User Modal */}
        {showEditForm && (
          <EditUserForm
            user={user}
            show={showEditForm}
            onClose={() => setShowEditForm(false)}
            onUserUpdated={handleUserUpdated}
            currentUser={currentUser}
          />
        )}
      </>
    );
  }

  // Render card view for mobile
  return (
    <>
      <div className={`${styles.card} ${selected ? styles.cardSelected : ""}`}>
        <div className={styles.cardHeader}>
          <div className={styles.cardCheckbox}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={selected}
              onChange={handleSelectionChange}
              disabled={user.email === currentUser.email}
            />
            <div className={styles.cardEmail}>
              {user.email}
              {user.email === currentUser.email && (
                <span className={styles.youBadge}>You</span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.cardBody}>
          <div className={styles.cardField}>
            <span className={styles.cardLabel}>Name:</span>
            <span className={styles.cardValue}>
              {user.name || <span className={styles.emptyText}>No name set</span>}
            </span>
          </div>

          <div className={styles.cardField}>
            <span className={styles.cardLabel}>Privileges:</span>
            <div className={styles.cardBadges}>
              {getPrivilegeBadges(user.privileges)}
            </div>
          </div>

          <div className={styles.cardField}>
            <span className={styles.cardLabel}>Features:</span>
            <div className={styles.cardBadges}>
              {getFeatureBadges(user.features)}
            </div>
          </div>
        </div>

        <div className={styles.cardActions}>
          <button
            className={`${styles.cardButton} ${styles.cardButtonPrimary}`}
            onClick={handleEdit}
            disabled={!canEdit()}
            title={canEdit() ? "Edit user" : "You cannot edit this user"}
          >
            <FaEdit /> Edit
          </button>

          <button
            className={`${styles.cardButton} ${styles.cardButtonDanger}`}
            onClick={() => onDelete(user)}
            disabled={!canDelete()}
            title={
              canDelete() ? "Delete user" : "You cannot delete this user"
            }
          >
            <FaTrash /> Delete
          </button>
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditForm && (
        <EditUserForm
          user={user}
          show={showEditForm}
          onClose={() => setShowEditForm(false)}
          onUserUpdated={handleUserUpdated}
          currentUser={currentUser}
        />
      )}
    </>
  );
};

UserListItem.propTypes = {
  user: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  viewMode: PropTypes.oneOf(['table', 'card']).isRequired
};

export default UserListItem;
